import Box from '@mui/material/Box';
import { DataGrid } from '@mui/x-data-grid';
import Button from '@mui/material/Button';
import Stack from '@mui/material/Stack';
import Typography from '@mui/material/Typography';
import { Grid } from '@mui/material';
import FormControl from '@mui/material/FormControl';
import Snackbar from '@mui/material/Snackbar';
import Alert from '@mui/material/Alert';
import TextField from '@mui/material/TextField';

import { useState, useEffect, useMemo } from 'react';
import { CSVLink } from 'react-csv';
import axios from '../../../api/axios';

import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import CalendarMonthIcon from '@mui/icons-material/CalendarMonth';
import PersonIcon from '@mui/icons-material/Person';
import StorefrontIcon from '@mui/icons-material/Storefront';


const URL = "./dealership/getUserWarrantyCommissionDetailsRM";


function ViewRelationshipCommission() {

    // =========================================================
    // STATE
    // =========================================================

    const [dataList, setDataList] = useState([]);

    const [accountName, setAccountName] = useState('');
    const [fromDate, setFromDate] = useState('');
    const [toDate, setToDate] = useState('');

    const [alertOpen, setAlertopen] = useState(false);
    const [severity, setSeverity] = useState('info');
    const [message, setMessage] = useState('');

    /*
        VIEW LEVEL

        months
            ↓
        managers
            ↓
        dealerships
    */
    const [viewLevel, setViewLevel] = useState('months');

    const [selectedMonth, setSelectedMonth] = useState(null);

    const [selectedManager, setSelectedManager] = useState(null);


    // =========================================================
    // HELPERS
    // =========================================================

    const parseNumber = (value) => {

        if (
            value === null ||
            value === undefined ||
            value === ''
        ) {
            return 0;
        }

        if (typeof value === 'number') {
            return value;
        }

        const cleaned = value
            .toString()
            .replace(/,/g, '')
            .replace(/[$%]/g, '')
            .trim();

        const parsed = parseFloat(cleaned);

        return Number.isNaN(parsed) ? 0 : parsed;
    };


    const formatCurrency = (value) => {

        const number = parseNumber(value);

        return `$${number.toLocaleString(undefined, {
            minimumFractionDigits: 2,
            maximumFractionDigits: 2
        })}`;
    };


    const getManagerName = (row) => {

        const firstName = row.firstname || '';
        const lastName = row.lastname || '';

        const fullName =
            `${firstName} ${lastName}`.trim();

        return fullName || 'Unknown Manager';
    };


    const getCommissionEarned = (row) => {

        /*
            Use API commissionEarned if available.
        */

        if (
            row.commissionEarned !== null &&
            row.commissionEarned !== undefined &&
            row.commissionEarned !== ''
        ) {
            return parseNumber(row.commissionEarned);
        }

        const cost =
            parseNumber(row.totalCost);

        const percentage =
            parseNumber(row.commissionPerc);

        return (cost * percentage) / 100;
    };


    const getMonthLabel = (month) => {

        if (!month) {
            return '';
        }

        const parts = month.split('-');

        if (parts.length !== 2) {
            return month;
        }

        const year = parts[0];

        const monthNumber =
            parseInt(parts[1], 10);

        if (!monthNumber) {
            return month;
        }

        const date =
            new Date(
                year,
                monthNumber - 1,
                1
            );

        return date.toLocaleString(
            'en-US',
            {
                month: 'long',
                year: 'numeric'
            }
        );
    };


    const getCommissionPercentage = (rows) => {

        const percentages = [
            ...new Set(
                rows
                    .map(row =>
                        parseNumber(
                            row.commissionPerc
                        )
                    )
                    .filter(
                        value =>
                            value !== null &&
                            value !== undefined
                    )
            )
        ];

        if (percentages.length === 0) {
            return '-';
        }

        if (percentages.length === 1) {
            return `${percentages[0]}%`;
        }

        return 'Multiple';
    };


    const createSummary = (rows) => {

        const totalValue =
            rows.reduce(
                (sum, row) =>
                    sum +
                    parseNumber(
                        row.totalCost
                    ),
                0
            );


        const warrantyCount =
            rows.reduce(
                (sum, row) =>
                    sum +
                    parseNumber(
                        row.warrantyCount
                    ),
                0
            );


        const commissionEarned =
            rows.reduce(
                (sum, row) =>
                    sum +
                    getCommissionEarned(row),
                0
            );


        return {

            totalValue,

            warrantyCount,

            commissionEarned,

            commissionPerc:
                getCommissionPercentage(rows)
        };
    };


    // =========================================================
    // ALERT
    // =========================================================

    const showAlert = (type, text) => {

        setSeverity(type);

        setMessage(text);

        setAlertopen(true);
    };


    const handleCloseSnack = (
        event,
        reason
    ) => {

        if (reason === 'clickaway') {
            return;
        }

        setAlertopen(false);
    };


    // =========================================================
    // LOAD INITIAL DATA
    // =========================================================

    useEffect(() => {

        loadData();

    }, []);


    const loadData = async () => {

        try {

            const response =
                await axios.post(URL);


            if (
                response.data.status === 401
            ) {

                setDataList([]);

                showAlert(
                    'error',
                    'Unauthorized access.'
                );

                return;
            }


            const responseData =
                response.data.data || [];


            const dataWithIndex =
                responseData.map(
                    (item, index) => ({

                        slNo: index + 1,

                        id:
                            `detail-${index + 1}`,

                        ...item

                    })
                );


            setDataList(
                dataWithIndex
            );


            // Reset navigation

            setViewLevel('months');

            setSelectedMonth(null);

            setSelectedManager(null);

        }
        catch (err) {

            console.log(
                "Error fetching data:",
                err
            );

            setDataList([]);

            showAlert(
                'error',
                'Error fetching warranty commission data.'
            );
        }
    };


    // =========================================================
    // DATE FILTER API
    // =========================================================

    const loadDateData = async () => {

        if (
            fromDate === "" ||
            toDate === ""
        ) {

            showAlert(
                'warning',
                'Both From Date and To Date are required.'
            );

            return;
        }


        if (
            new Date(toDate) <
            new Date(fromDate)
        ) {

            showAlert(
                'warning',
                'To Date must be greater than or equal to From Date.'
            );

            return;
        }


        try {

            const response =
                await axios.post(
                    "dealership/getDateUserWarrantyCommissionDetailsRM",
                    {
                        dealership: '',
                        fromDate,
                        toDate
                    }
                );


            if (
                response.data.status === 401
            ) {

                setDataList([]);

                showAlert(
                    'error',
                    'Unauthorized access.'
                );

                return;
            }


            const responseData =
                response.data.data || [];


            const dataWithIndex =
                responseData.map(
                    (item, index) => ({

                        slNo: index + 1,

                        id:
                            `detail-${index + 1}`,

                        ...item

                    })
                );


            setDataList(
                dataWithIndex
            );


            // Return to month screen

            setViewLevel('months');

            setSelectedMonth(null);

            setSelectedManager(null);

        }
        catch (err) {

            console.log(
                "Error fetching date filtered data:",
                err
            );

            setDataList([]);

            showAlert(
                'error',
                'Error fetching filtered data.'
            );
        }
    };


    // =========================================================
    // RESET
    // =========================================================

    const handleReset = () => {

        setAccountName('');

        setFromDate('');

        setToDate('');

        setViewLevel('months');

        setSelectedMonth(null);

        setSelectedManager(null);

        loadData();
    };


    // =========================================================
    // DEALERSHIP FILTER
    // =========================================================

    const filteredRawRows = useMemo(() => {

        let filtered =
            [...dataList];


        if (
            accountName.trim()
        ) {

            const searchValue =
                accountName
                    .toLowerCase()
                    .trim();


            filtered =
                filtered.filter(
                    row =>
                        (
                            row.tradeName || ''
                        )
                            .toLowerCase()
                            .includes(
                                searchValue
                            )
                );
        }


        return filtered;

    }, [
        dataList,
        accountName
    ]);


    // =========================================================
    // MONTH DATA
    // =========================================================

    const monthRows = useMemo(() => {

        const monthMap =
            new Map();


        filteredRawRows.forEach(row => {

            const month =
                row.Month ||
                'Unknown Month';


            if (
                !monthMap.has(month)
            ) {

                monthMap.set(
                    month,
                    []
                );
            }


            monthMap
                .get(month)
                .push(row);

        });


        return [
            ...monthMap.entries()
        ]
            .sort(
                (a, b) =>
                    b[0].localeCompare(
                        a[0]
                    )
            );

    }, [
        filteredRawRows
    ]);


    // =========================================================
    // SELECTED MONTH ROWS
    // =========================================================

    const selectedMonthRows =
        useMemo(() => {

            if (!selectedMonth) {
                return [];
            }


            return filteredRawRows.filter(
                row =>
                    (
                        row.Month ||
                        'Unknown Month'
                    ) ===
                    selectedMonth
            );

        }, [
            filteredRawRows,
            selectedMonth
        ]);


    // =========================================================
    // MANAGER DATA
    // =========================================================

    const managerRows = useMemo(() => {

        const managerMap =
            new Map();


        selectedMonthRows.forEach(row => {

            const managerId =
                row.managerId ||
                'unknown';


            if (
                !managerMap.has(
                    managerId
                )
            ) {

                managerMap.set(
                    managerId,
                    []
                );
            }


            managerMap
                .get(managerId)
                .push(row);

        });


        return [
            ...managerMap.entries()
        ]
            .sort(
                (a, b) => {

                    const nameA =
                        getManagerName(
                            a[1][0]
                        ).toLowerCase();


                    const nameB =
                        getManagerName(
                            b[1][0]
                        ).toLowerCase();


                    return nameA.localeCompare(
                        nameB
                    );
                }
            );

    }, [
        selectedMonthRows
    ]);


    // =========================================================
    // SELECTED MANAGER ROWS
    // =========================================================

    const selectedManagerRows =
        useMemo(() => {

            if (
                !selectedManager
            ) {

                return [];
            }


            return selectedMonthRows.filter(
                row =>
                    String(
                        row.managerId ||
                        'unknown'
                    ) ===
                    String(
                        selectedManager.managerId
                    )
            );

        }, [
            selectedMonthRows,
            selectedManager
        ]);


    // =========================================================
    // NAVIGATION
    // =========================================================

    const openMonth = (month) => {

        setSelectedMonth(month);

        setSelectedManager(null);

        setViewLevel('managers');
    };


    const openManager = (
        managerId,
        manager
    ) => {

        setSelectedManager({

            managerId,

            name:
                getManagerName(
                    manager
                )

        });


        setViewLevel(
            'dealerships'
        );
    };


    const goBack = () => {

        if (
            viewLevel ===
            'dealerships'
        ) {

            setSelectedManager(null);

            setViewLevel(
                'managers'
            );

            return;
        }


        if (
            viewLevel ===
            'managers'
        ) {

            setSelectedMonth(null);

            setViewLevel(
                'months'
            );

            return;
        }
    };


    // =========================================================
    // CURRENT ROWS
    // =========================================================

    const currentRows =
        useMemo(() => {

            // -------------------------------------------------
            // MONTH SCREEN
            // -------------------------------------------------

            if (
                viewLevel ===
                'months'
            ) {

                return monthRows.map(
                    (
                        [month, rows],
                        index
                    ) => {

                        const summary =
                            createSummary(
                                rows
                            );


                        return {

                            id:
                                `month-${month}`,

                            rowType:
                                'month',

                            slNo:
                                index + 1,

                            Month:
                                month,

                            monthLabel:
                                getMonthLabel(
                                    month
                                ),

                            relationshipManagerName:
                                '',

                            tradeName:
                                `${rows.length} record(s)`,

                            warrantyCount:
                                summary.warrantyCount,

                            totalCost:
                                summary.totalValue,

                            commissionPerc:
                                summary.commissionPerc,

                            commissionEarned:
                                summary.commissionEarned,

                            recordCount:
                                rows.length
                        };

                    }
                );
            }


            // -------------------------------------------------
            // MANAGER SCREEN
            // -------------------------------------------------

            if (
                viewLevel ===
                'managers'
            ) {

                return managerRows.map(
                    (
                        [managerId, rows],
                        index
                    ) => {

                        const manager =
                            rows[0];


                        const summary =
                            createSummary(
                                rows
                            );


                        return {

                            id:
                                `manager-${selectedMonth}-${managerId}`,

                            rowType:
                                'manager',

                            slNo:
                                index + 1,

                            Month:
                                selectedMonth,

                            monthLabel:
                                getMonthLabel(
                                    selectedMonth
                                ),

                            managerId,

                            relationshipManagerName:
                                getManagerName(
                                    manager
                                ),

                            firstname:
                                manager.firstname,

                            lastname:
                                manager.lastname,

                            tradeName:
                                `${rows.length} dealership record(s)`,

                            warrantyCount:
                                summary.warrantyCount,

                            totalCost:
                                summary.totalValue,

                            commissionPerc:
                                summary.commissionPerc,

                            commissionEarned:
                                summary.commissionEarned,

                            recordCount:
                                rows.length
                        };

                    }
                );
            }


            // -------------------------------------------------
            // DEALERSHIP SCREEN
            // -------------------------------------------------

            if (
                viewLevel ===
                'dealerships'
            ) {

                return selectedManagerRows.map(
                    (
                        row,
                        index
                    ) => ({

                        ...row,

                        id:
                            `dealership-${selectedMonth}-${selectedManager.managerId}-${row.dealershipId}-${index}`,

                        rowType:
                            'detail',

                        slNo:
                            index + 1,

                        relationshipManagerName:
                            selectedManager.name

                    })
                );
            }


            return [];

        }, [
            viewLevel,
            monthRows,
            managerRows,
            selectedMonth,
            selectedManager,
            selectedManagerRows
        ]);


    // =========================================================
    // CSV DATA
    // =========================================================

    const csvData = useMemo(() => {

        let rowsForCSV =
            filteredRawRows;


        if (
            viewLevel ===
            'managers'
        ) {

            rowsForCSV =
                selectedMonthRows;
        }


        if (
            viewLevel ===
            'dealerships'
        ) {

            rowsForCSV =
                selectedManagerRows;
        }


        return rowsForCSV.map(
            row => ({

                ID:
                    row.slNo,

                Month:
                    row.Month,

                RelationshipManager:
                    getManagerName(
                        row
                    ),

                Dealership:
                    row.tradeName,

                WarrantySold:
                    row.warrantyCount,

                TotalValue:
                    parseNumber(
                        row.totalCost
                    ),

                CommissionPercentage:
                    parseNumber(
                        row.commissionPerc
                    ),

                CommissionEarned:
                    getCommissionEarned(
                        row
                    )

            })
        );

    }, [
        filteredRawRows,
        selectedMonthRows,
        selectedManagerRows,
        viewLevel
    ]);


    // =========================================================
    // PAGE TITLE
    // =========================================================

    const pageTitle =
        viewLevel === 'months'
            ? 'Relationship Manager Commission'
            : viewLevel === 'managers'
                ? `${getMonthLabel(selectedMonth)} - Relationship Managers`
                : `${selectedManager?.name || 'Relationship Manager'} - Dealerships`;


    // =========================================================
    // BREADCRUMB
    // =========================================================

    const breadcrumb =
        viewLevel === 'months'
            ? null
            : (
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: 1,
                        mb: 1
                    }}
                >

                    <Button
                        startIcon={
                            <ArrowBackIcon />
                        }
                        onClick={goBack}
                        sx={{
                            color: '#0d2365',
                            textTransform:
                                'none',
                            fontWeight:
                                600
                        }}
                    >
                        Back
                    </Button>


                    <Typography
                        sx={{
                            color:
                                'text.secondary'
                        }}
                    >
                        {viewLevel ===
                        'managers'
                            ? getMonthLabel(
                                selectedMonth
                            )
                            : `${getMonthLabel(selectedMonth)} / ${selectedManager?.name || ''}`
                        }
                    </Typography>

                </Box>
            );


    // =========================================================
    // COLUMNS
    // =========================================================

    const columns = [

        {
    field: 'slNo',
    headerName: 'ID',
    width: 80,
    sortable: false,
    align: 'center',
    headerAlign: 'center',
    renderCell: (params) => (
        <Typography
            sx={{
                fontWeight:
                    params.row.rowType === 'detail'
                        ? 'normal'
                        : 700
            }}
        >
            {params.value}
        </Typography>
    )
},


        {
            field:
                'relationshipManagerName',

            headerName:
                viewLevel ===
                'months'
                    ? 'Month'
                    : 'Relationship Manager',

            width: 250,

            renderCell:
                (params) => {

                    const row =
                        params.row;


                    if (
                        viewLevel ===
                        'months'
                    ) {

                        return (
                            <Button
                                onClick={() =>
                                    openMonth(
                                        row.Month
                                    )
                                }
                                sx={{
                                    justifyContent:
                                        'flex-start',
                                    textTransform:
                                        'none',
                                    color:
                                        '#0d2365',
                                    fontWeight:
                                        700,
                                    padding: 0,
                                    minWidth: 0
                                }}
                            >

                                <CalendarMonthIcon
                                    sx={{
                                        mr: 1,
                                        fontSize:
                                            20
                                    }}
                                />

                                {row.monthLabel}

                            </Button>
                        );
                    }


                    if (
                        viewLevel ===
                        'managers'
                    ) {

                        return (
                            <Button
                                onClick={() =>
                                    openManager(
                                        row.managerId,
                                        row
                                    )
                                }
                                sx={{
                                    justifyContent:
                                        'flex-start',
                                    textTransform:
                                        'none',
                                    color:
                                        '#0d2365',
                                    fontWeight:
                                        600,
                                    padding: 0,
                                    minWidth: 0
                                }}
                            >

                                <PersonIcon
                                    sx={{
                                        mr: 1,
                                        fontSize:
                                            20
                                    }}
                                />

                                {
                                    row.relationshipManagerName
                                }

                            </Button>
                        );
                    }


                    return (
                        <Box>
                            {
                                row.relationshipManagerName
                            }
                        </Box>
                    );
                }
        },


        {
            field:
                'tradeName',

            headerName:
                'Dealership',

            width: 280,

            renderCell:
                (params) => {

                    const row =
                        params.row;


                    if (
                        row.rowType ===
                        'month'
                    ) {

                        return (
                            <Typography
                                sx={{
                                    color:
                                        'text.secondary'
                                }}
                            >
                                {row.recordCount}
                                {' '}
                                record(s)
                            </Typography>
                        );
                    }


                    if (
                        row.rowType ===
                        'manager'
                    ) {

                        return (
                            <Typography
                                sx={{
                                    color:
                                        'text.secondary',
                                    fontStyle:
                                        'italic'
                                }}
                            >
                                {row.recordCount}
                                {' '}
                                dealership record(s)
                            </Typography>
                        );
                    }


                    return (
                        <Box
                            sx={{
                                display:
                                    'flex',
                                alignItems:
                                    'center'
                            }}
                        >

                            <StorefrontIcon
                                sx={{
                                    mr: 1,
                                    fontSize:
                                        18
                                }}
                            />

                            {
                                row.tradeName
                            }

                        </Box>
                    );
                }
        },


        {
            field:
                'warrantyCount',

            headerName:
                'Warranty Sold',

            width: 150,

            type:
                'number',

            align:
                'right',

            headerAlign:
                'right',

            renderCell:
                (params) => (

                    <Typography
                        sx={{
                            fontWeight:
                                params.row.rowType ===
                                'detail'
                                    ? 'normal'
                                    : 700
                        }}
                    >
                        {
                            params.value ??
                            0
                        }
                    </Typography>
                )
        },


        {
            field:
                'Month',

            headerName:
                'Year-Month',

            width: 140,

            renderCell:
                (params) => (
                    params.value ||
                    '-'
                )
        },


        {
            field:
                'totalCost',

            headerName:
                'Total Value',

            width: 180,

            type:
                'number',

            align:
                'right',

            headerAlign:
                'right',

            renderCell:
                (params) => (

                    <Typography
                        sx={{
                            fontWeight:
                                params.row.rowType ===
                                'detail'
                                    ? 'normal'
                                    : 700
                        }}
                    >
                        {
                            formatCurrency(
                                params.value
                            )
                        }
                    </Typography>
                )
        },


        {
            field:
                'commissionPerc',

            headerName:
                'Commission %',

            width: 150,

            align:
                'right',

            headerAlign:
                'right',

            renderCell:
                (params) => (

                    <Typography
                        sx={{
                            fontWeight:
                                params.row.rowType ===
                                'detail'
                                    ? 'normal'
                                    : 700
                        }}
                    >
                        {
                            params.value ||
                            '-'
                        }
                    </Typography>
                )
        },


        {
            field:
                'commissionEarned',

            headerName:
                'Commission Earned',

            width: 190,

            type:
                'number',

            align:
                'right',

            headerAlign:
                'right',

            renderCell:
                (params) => (

                    <Typography
                        sx={{
                            fontWeight:
                                params.row.rowType ===
                                'detail'
                                    ? 'normal'
                                    : 700
                        }}
                    >
                        {
                            formatCurrency(
                                params.value
                            )
                        }
                    </Typography>
                )
        }

    ];


    // =========================================================
    // RENDER
    // =========================================================

    return (

        <div
            style={{
                marginTop: '100px',
                padding: '0px'
            }}
        >

            <Box
                component="main"
                sx={{
                    flexGrow: 1,
                    p: 0
                }}
            >

                {/* =================================================
                    FILTER AREA
                ================================================= */}

                <Box
                    sx={{
                        flexGrow: 1,
                        padding: '10px'
                    }}
                >

                    <Grid
                        container
                        spacing={2}
                    >

                        {/* DEALERSHIP */}

                        <Grid
                            item
                            xs={12}
                            md={3}
                        >

                            <FormControl
                                fullWidth
                            >

                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        mb: 0.5
                                    }}
                                >
                                    Dealership
                                </Typography>


                                <TextField
                                    label="Search by Dealership Name"
                                    variant="outlined"
                                    value={
                                        accountName
                                    }
                                    onChange={
                                        (e) =>
                                            setAccountName(
                                                e.target.value
                                            )
                                    }
                                    fullWidth
                                />

                            </FormControl>

                        </Grid>


                        {/* FROM DATE */}

                        <Grid
                            item
                            xs={12}
                            md={2}
                        >

                            <FormControl
                                fullWidth
                            >

                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        mb: 0.5
                                    }}
                                >
                                    From Date
                                </Typography>


                                <TextField
                                    variant="outlined"
                                    type="date"
                                    value={
                                        fromDate
                                    }
                                    onChange={
                                        (e) =>
                                            setFromDate(
                                                e.target.value
                                            )
                                    }
                                    fullWidth
                                    InputLabelProps={{
                                        shrink:
                                            true
                                    }}
                                />

                            </FormControl>

                        </Grid>


                        {/* TO DATE */}

                        <Grid
                            item
                            xs={12}
                            md={2}
                        >

                            <FormControl
                                fullWidth
                            >

                                <Typography
                                    variant="subtitle2"
                                    sx={{
                                        mb: 0.5
                                    }}
                                >
                                    To Date
                                </Typography>


                                <TextField
                                    variant="outlined"
                                    type="date"
                                    value={
                                        toDate
                                    }
                                    onChange={
                                        (e) =>
                                            setToDate(
                                                e.target.value
                                            )
                                    }
                                    fullWidth
                                    InputLabelProps={{
                                        shrink:
                                            true
                                    }}
                                />

                            </FormControl>

                        </Grid>


                        <Grid
                            item
                            xs={12}
                            md={5}
                        />

                    </Grid>

                </Box>


                {/* =================================================
                    DATA GRID
                ================================================= */}

                <Box
                    sx={{
                        width: '100%',
                        overflowX: 'auto'
                    }}
                >

                    <DataGrid

                        rows={
                            currentRows
                        }

                        columns={
                            columns
                        }

                        getRowId={
                            (row) =>
                                row.id
                        }

                        pageSizeOptions={[
                            5,
                            10,
                            25,
                            50,
                            100
                        ]}

                        pagination

                        initialState={{
                            pagination: {
                                paginationModel: {
                                    pageSize: 100,
                                    page: 0
                                }
                            }
                        }}

                        disableRowSelectionOnClick


                        slots={{
                            toolbar: () => (

                                <Box
                                    sx={{
                                        padding: 2,
                                        display:
                                            'flex',
                                        alignItems:
                                            'center',
                                        borderBottom:
                                            '0.2px solid grey',
                                        gap: 1
                                    }}
                                >

                                    {/* BACK BUTTON */}

                                    {viewLevel !==
                                        'months' && (

                                        <Button
                                            startIcon={
                                                <ArrowBackIcon />
                                            }
                                            onClick={
                                                goBack
                                            }
                                            sx={{
                                                color:
                                                    '#0d2365',
                                                textTransform:
                                                    'none',
                                                fontWeight:
                                                    600,
                                                mr: 1
                                            }}
                                        >
                                            Back
                                        </Button>

                                    )}


                                    {/* TITLE */}

                                    <Typography
                                        variant="h6"
                                        sx={{
                                            fontWeight:
                                                'medium',
                                            color:
                                                '#0d2365',
                                            fontFamily:
                                                'Montserrat, sans-serif'
                                        }}
                                    >
                                        {
                                            pageTitle
                                        }
                                    </Typography>


                                    {/* RIGHT BUTTONS */}

                                    <Box
                                        sx={{
                                            marginLeft:
                                                'auto',
                                            display:
                                                'flex',
                                            gap: 1
                                        }}
                                    >

                                        <Button
                                            onClick={
                                                loadDateData
                                            }
                                            sx={{
                                                backgroundColor:
                                                    '#0d2365',
                                                color:
                                                    'white',
                                                borderRadius:
                                                    '10px',
                                                '&:hover':
                                                {
                                                    backgroundColor:
                                                        '#0d2365'
                                                }
                                            }}
                                        >
                                            Fetch Data
                                        </Button>


                                        <Button
                                            onClick={
                                                handleReset
                                            }
                                            sx={{
                                                backgroundColor:
                                                    '#0d2365',
                                                color:
                                                    'white',
                                                borderRadius:
                                                    '10px',
                                                '&:hover':
                                                {
                                                    backgroundColor:
                                                        '#0d2365'
                                                }
                                            }}
                                        >
                                            Reset Data
                                        </Button>


                                        <Button
                                            variant="outlined"
                                            sx={{
                                                backgroundColor:
                                                    '#0d2365',
                                                color:
                                                    'white',
                                                borderRadius:
                                                    '10px',
                                                borderColor:
                                                    '#0d2365',
                                                '&:hover':
                                                {
                                                    backgroundColor:
                                                        '#0d2365',
                                                    borderColor:
                                                        '#0d2365'
                                                }
                                            }}
                                        >

                                            <CSVLink
                                                data={
                                                    csvData
                                                }
                                                filename="relationship-manager-commission.csv"
                                                style={{
                                                    textDecoration:
                                                        'none',
                                                    color:
                                                        'inherit'
                                                }}
                                            >
                                                Export
                                            </CSVLink>

                                        </Button>

                                    </Box>

                                </Box>
                            )
                        }}


                        sx={{

                            minHeight:
                                400,


                            '& .MuiDataGrid-columnHeaderTitle':
                            {
                                color:
                                    '#0d2365',

                                fontWeight:
                                    'bold'
                            },


                            '& .MuiDataGrid-row':
                            {
                                cursor:
                                    'default'
                            },


                            '& .month-row':
                            {
                                backgroundColor:
                                    '#eef2f7',

                                fontWeight:
                                    'bold'
                            },


                            '& .manager-row':
                            {
                                backgroundColor:
                                    '#f8fafc',

                                fontWeight:
                                    600
                            },


                            '& .detail-row':
                            {
                                backgroundColor:
                                    '#ffffff'
                            },


                            '& .MuiDataGrid-row:hover':
                            {
                                backgroundColor:
                                    '#f1f5f9'
                            }

                        }}

                    />

                </Box>

            </Box>


            {/* =================================================
                HIDDEN TOTAL
            ================================================= */}

            <Stack
                spacing={3}
                sx={{
                    display: 'none'
                }}
            >

                <Stack
                    direction="row"
                    spacing={3}
                >

                    <div>

                        <Typography
                            variant="subtitle2"
                        >
                            Total Billable Cost
                        </Typography>


                        <TextField
                            variant="outlined"
                            disabled
                            value="$0.00"
                        />

                    </div>

                </Stack>

            </Stack>


            {/* =================================================
                SNACKBAR
            ================================================= */}

            <Snackbar
                open={
                    alertOpen
                }
                autoHideDuration={
                    6000
                }
                onClose={
                    handleCloseSnack
                }
            >

                <Alert
                    onClose={
                        handleCloseSnack
                    }
                    severity={
                        severity
                    }
                    variant="filled"
                    sx={{
                        width:
                            '100%'
                    }}
                >
                    {
                        message
                    }
                </Alert>

            </Snackbar>

        </div>
    );
}


export default ViewRelationshipCommission;